import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Seo from "@/components/Seo";
import { LOCAL_SEO_PAGES } from "@/pages/public/seo/localSeoContent";
import { Check, MapPin, Phone, Mail, ArrowRight, HelpCircle, Star, Building2, Link2 } from "lucide-react";

const BASE = "https://yosimediastudio.com";
const PHONE_TEL = "+12562888270";
const PHONE_HUMAN = "+1 (256) 288-8270";
const EMAIL = "info@yosimediastudio.com";

const LABELS = {
  en: {
    breadcrumbHome: "Home",
    breadcrumbServices: "Services",
    callUs: "Call us",
    emailUs: "Email us",
    location: "Huntsville, Alabama",
    requestQuote: "Request a Quote",
    talkToUs: "Talk to Us",
    seeAllServices: "See all services",
    languageSwitch: "En español",
    localFocus: "Local focus",
    included: "Included",
    process: "Process",
    areas: "Service area",
  },
  es: {
    breadcrumbHome: "Inicio",
    breadcrumbServices: "Servicios",
    callUs: "Llámenos",
    emailUs: "Escríbanos",
    location: "Huntsville, Alabama",
    requestQuote: "Solicitar Cotización",
    talkToUs: "Hablemos",
    seeAllServices: "Ver todos los servicios",
    languageSwitch: "In English",
    localFocus: "Enfoque local",
    included: "Incluido",
    process: "Proceso",
    areas: "Zona de servicio",
  },
};

/** Build the full set of JSON-LD structured data blocks for a landing page. */
function buildSchemas({ lang, page, canonical, altHref }) {
  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.h1,
    description: page.description,
    serviceType: page.serviceType,
    areaServed: [
      { "@type": "City", name: "Huntsville", containedInPlace: { "@type": "State", name: "Alabama" } },
      { "@type": "City", name: "Madison" },
      { "@type": "City", name: "Decatur" },
      { "@type": "City", name: "Athens" },
    ],
    provider: {
      "@type": "LocalBusiness",
      name: "Yosi Media Studio",
      telephone: PHONE_TEL,
      email: EMAIL,
      url: BASE,
      areaServed: { "@type": "State", name: "Alabama" },
      address: {
        "@type": "PostalAddress",
        addressLocality: "Huntsville",
        addressRegion: "AL",
        addressCountry: "US",
      },
    },
    url: canonical,
  };
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: LABELS[lang].breadcrumbHome, item: BASE + (lang === "es" ? "/es" : "/") },
      { "@type": "ListItem", position: 2, name: LABELS[lang].breadcrumbServices, item: BASE + (lang === "es" ? "/es/servicios" : "/services") },
      { "@type": "ListItem", position: 3, name: page.h1, item: canonical },
    ],
  };
  return [service, faq, breadcrumb];
}

/**
 * LocalSeoLanding — dedicated Huntsville-local landing page.
 *
 * Renders one language variant of one service; the sibling-language page
 * always exists so hreflang alternates resolve.
 */
export default function LocalSeoLanding({ serviceKey, lang }) {
  const page = LOCAL_SEO_PAGES[serviceKey][lang];
  const other = LOCAL_SEO_PAGES[serviceKey][lang === "en" ? "es" : "en"];
  const L = LABELS[lang];

  const canonical = `${BASE}${lang === "es" ? `/es/servicios/${page.slug}` : `/services/${page.slug}`}`;
  const altHref = `${BASE}${lang === "es" ? `/services/${other.slug}` : `/es/servicios/${other.slug}`}`;
  const switchTo = lang === "es" ? `/services/${other.slug}` : `/es/servicios/${other.slug}`;

  const hreflangAlternates = [
    { hreflang: "en-us", href: `${BASE}/services/${LOCAL_SEO_PAGES[serviceKey].en.slug}` },
    { hreflang: "es-us", href: `${BASE}/es/servicios/${LOCAL_SEO_PAGES[serviceKey].es.slug}` },
    { hreflang: "x-default", href: `${BASE}/services/${LOCAL_SEO_PAGES[serviceKey].en.slug}` },
  ];

  const schemas = buildSchemas({ lang, page, canonical, altHref });
  const contactQs = `?service=${encodeURIComponent(page.serviceType)}&lang=${lang}`;

  return (
    <MarketingLayout>
      <Seo
        title={page.title}
        description={page.description}
        canonical={canonical}
        lang={lang}
        hreflangAlternates={hreflangAlternates}
        jsonLd={schemas}
      />

      {/* Breadcrumb + language switcher */}
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-6 sm:px-8 pt-6 flex items-center justify-between text-xs text-slate-400" data-testid="seo-breadcrumb">
        <ol className="flex items-center gap-2">
          <li><Link to={lang === "es" ? "/es" : "/"} className="hover:text-blue-400">{L.breadcrumbHome}</Link></li>
          <li aria-hidden>›</li>
          <li><Link to={lang === "es" ? "/es/servicios" : "/services"} className="hover:text-blue-400">{L.breadcrumbServices}</Link></li>
          <li aria-hidden>›</li>
          <li className="text-slate-200">{page.h1}</li>
        </ol>
        <Link to={switchTo} className="hidden sm:inline-flex items-center gap-1 text-blue-400 hover:text-blue-300" data-testid="seo-lang-switch">
          {L.languageSwitch}
        </Link>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid={`seo-hero-${serviceKey}-${lang}`}>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-40 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-10 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs font-medium mb-5">
              <MapPin className="w-3 h-3" /> {L.location}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-5">
              {page.h1}
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">{page.intro}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={`/contact${contactQs}`} data-testid="seo-hero-cta" className="btn-scale h-12 px-6 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center gap-2 shadow-xl shadow-blue-500/40">
                {L.requestQuote} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${PHONE_TEL}`} data-testid="seo-hero-call" className="btn-scale h-12 px-6 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-200 font-semibold text-sm inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> {PHONE_HUMAN}
              </a>
            </div>
            <div className="mt-6 text-xs text-slate-500">{page.priceHint}</div>
          </div>
          <aside className="lg:col-span-5" aria-label="Contact card">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-4">
              <div className="flex items-center gap-3 text-slate-200">
                <span className="w-9 h-9 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><Phone className="w-4 h-4" /></span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{L.callUs}</div>
                  <a href={`tel:${PHONE_TEL}`} className="text-sm font-semibold hover:text-blue-400">{PHONE_HUMAN}</a>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-200">
                <span className="w-9 h-9 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><Mail className="w-4 h-4" /></span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{L.emailUs}</div>
                  <a href={`mailto:${EMAIL}`} className="text-sm font-semibold hover:text-blue-400">{EMAIL}</a>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-200">
                <span className="w-9 h-9 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><MapPin className="w-4 h-4" /></span>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{L.areas}</div>
                  <div className="text-sm font-semibold">{L.location}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-4 h-4 text-blue-400 fill-blue-400" />)}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Why local */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-14" data-testid="seo-why-local">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">{L.localFocus}</div>
        <h2 className="font-display text-3xl font-bold mb-8">{page.whyLocalTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {page.bullets.map((b) => (
            <div key={b} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-5">
              <Check className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
              <span className="text-slate-200 text-sm leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="bg-slate-900/40 border-y border-slate-800/60 py-14" data-testid="seo-included">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">{L.included}</div>
          <h2 className="font-display text-3xl font-bold mb-8">{page.whatIncludedTitle}</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {page.list.map((item) => (
              <li key={item} className="rounded-xl border border-slate-800 bg-slate-950/60 p-5 flex items-start gap-3">
                <Check className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <span className="text-slate-200 text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-14" data-testid="seo-process">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">{L.process}</div>
        <h2 className="font-display text-3xl font-bold mb-8">{page.processTitle}</h2>
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {page.steps.map((step, i) => (
            <li key={step.t} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 relative">
              <div className="absolute -top-3 left-5 h-6 px-2.5 rounded-full bg-blue-500 text-[11px] font-bold text-white inline-flex items-center">{i + 1}</div>
              <h3 className="font-display font-semibold text-lg mt-3 mb-2">{step.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{step.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Industries (optional — only rendered when page.industries provided) */}
      {Array.isArray(page.industries) && page.industries.length > 0 && (
        <section className="bg-slate-900/40 border-y border-slate-800/60 py-14" data-testid="seo-industries">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Industries</div>
            <h2 className="font-display text-3xl font-bold mb-3">{page.industriesTitle}</h2>
            {page.industriesIntro && (
              <p className="text-slate-400 text-sm max-w-2xl mb-8 leading-relaxed">{page.industriesIntro}</p>
            )}
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {page.industries.map((name) => (
                <li key={name} className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-center" data-testid={`seo-industry-${name.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="w-9 h-9 mx-auto rounded-lg grid place-items-center mb-2 bg-blue-500/10 text-blue-300">
                    <Building2 className="w-4 h-4" strokeWidth={1.6} />
                  </div>
                  <div className="text-slate-100 text-sm font-semibold">{name}</div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Related services / internal links (optional) */}
      {Array.isArray(page.internalLinks) && page.internalLinks.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-14" data-testid="seo-internal-links">
          <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">Related services</div>
          <h2 className="font-display text-3xl font-bold mb-3">{page.internalLinksTitle}</h2>
          {page.internalLinksIntro && (
            <p className="text-slate-400 text-sm max-w-2xl mb-8 leading-relaxed">{page.internalLinksIntro}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {page.internalLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`seo-internal-${link.to.replace(/[/?=&]/g,'-')}`}
                className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover:border-blue-500/40 hover:bg-slate-900/70 transition-colors block group"
              >
                <div className="flex items-center gap-2 mb-2 text-blue-300">
                  <Link2 className="w-4 h-4" />
                  <h3 className="font-display font-semibold text-slate-100 text-base">{link.title}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{link.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-400 group-hover:text-blue-300">
                  Learn more <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Areas served */}
      <section className="bg-slate-900/40 border-y border-slate-800/60 py-14" data-testid="seo-areas">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <h2 className="font-display text-3xl font-bold mb-2">{page.areasTitle}</h2>
          <p className="text-slate-400 text-sm mb-8 max-w-2xl">
            {lang === "es"
              ? "Trabajamos en persona con clientes en toda el área metropolitana de Huntsville. Si su ciudad no aparece abajo, igual conversemos."
              : "We work in person with clients across the Huntsville metro. If your city isn't listed below, still get in touch."}
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {page.areas.map((area) => (
              <li key={area.name} className="rounded-xl border border-slate-800 bg-slate-950/60 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-slate-100">{area.name}</h3>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{area.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 sm:px-8 py-14" data-testid="seo-faq">
        <div className="text-xs uppercase tracking-widest text-blue-400 mb-2">FAQ</div>
        <h2 className="font-display text-3xl font-bold mb-8">{page.faqTitle}</h2>
        <div className="space-y-3">
          {page.faqs.map((f, i) => (
            <details key={f.q} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 group" data-testid={`seo-faq-${i}`}>
              <summary className="cursor-pointer flex items-center gap-2 text-slate-100 font-medium">
                <HelpCircle className="w-4 h-4 text-blue-400 shrink-0" />
                <span>{f.q}</span>
              </summary>
              <p className="text-sm text-slate-400 leading-relaxed mt-3 pl-6">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto text-center px-6 pb-20" data-testid="seo-cta">
        <h2 className="font-display text-3xl font-bold mb-4">{page.ctaTitle}</h2>
        <p className="text-slate-400 mb-8">{page.ctaBody}</p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to={`/contact${contactQs}`} className="btn-scale h-12 px-6 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm inline-flex items-center gap-2 shadow-xl shadow-blue-500/40">
            {L.requestQuote} <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={`tel:${PHONE_TEL}`} className="btn-scale h-12 px-6 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-200 font-semibold text-sm inline-flex items-center gap-2">
            <Phone className="w-4 h-4" /> {PHONE_HUMAN}
          </a>
        </div>
        <div className="mt-6">
          <Link to={lang === "es" ? "/es/servicios" : "/services"} className="text-xs text-blue-400 hover:text-blue-300">
            {L.seeAllServices} →
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
